import apiConfig from 'src/integration/config/apiConfig';
import useQueryKey from 'src/toolkit/utils/useQueryKey';
import { useStore } from 'src/integration/store/store';
import useReactQueryJava from 'src/integration/api/useReactQueryJava';
import { MIN_15 } from 'src/integration/config/constants';
import colors from 'src/integration/styles/colors';

interface FfrEnergyVolumesDto {
  id: number;
  date: string;
  time: string;
  mfrrUpEnergy: {
    value: number;
    unit: string;
  };
  mfrrDownEnergy: {
    value: number;
    unit: string;
  };
  afrrUpEnergy: {
    value: number;
    unit: string;
  };
  afrrDownEnergy: {
    value: number;
    unit: string;
  };
}

const emptyChartData = {
  labels: [],
  lineLabels: [],
  alignDualXAxis: true,
  datasets: [
    {
      label: 'mFRR up energy',
      data: [],
      backgroundColor: colors.chart.carminePink,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
    {
      label: 'mFRR down energy',
      data: [],
      backgroundColor: colors.chart.carminePinkLight,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
    {
      label: 'aFRR up energy',
      data: [],
      backgroundColor: colors.chart.pastelOrange,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
    {
      label: 'aFRR down energy',
      data: [],
      backgroundColor: colors.chart.pastelOrangeLight,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
  ],
};

const useFetchFfrEnergyVolumes = () => {
  const { settingsStore } = useStore();
  const { selectedSiteId, selectedDate } = settingsStore;

  const response = useReactQueryJava<FfrEnergyVolumesDto[], SimpleObject>({
    endpoint: apiConfig.api.endpointsJava.ffrEnergyVolumes,
    queryKey: useQueryKey('ffrEnergyVolumes', [selectedDate, selectedSiteId?.toString()]),
    method: 'GET',
    queryParams: {
      date: selectedDate,
      siteId: selectedSiteId,
    },
    opts: {
      enabled: !!selectedSiteId,
      refetchOnWindowFocus: false,
      refetchInterval: MIN_15,
    },
  });

  const chartData = response?.data ? {
    labels: response.data.map((item) => item.time),
    lineLabels: response.data.map((item) => item.time),
    alignDualXAxis: true,
    datasets: [
      {
        ...emptyChartData.datasets[0],
        data: response.data.map((item) => ({ x: item.time, y: item.mfrrUpEnergy?.value ?? 0 })),
      },
      {
        ...emptyChartData.datasets[1],
        data: response.data.map((item) => ({ x: item.time, y: item.mfrrDownEnergy?.value ?? 0 })),
      },
      {
        ...emptyChartData.datasets[2],
        data: response.data.map((item) => ({ x: item.time, y: item.afrrUpEnergy?.value ?? 0 })),
      },
      {
        ...emptyChartData.datasets[3],
        data: response.data.map((item) => ({ x: item.time, y: item.afrrDownEnergy?.value ?? 0 })),
      },
    ],
  } : emptyChartData;

  return {
    ...response,
    chartData,
  };
};

export default useFetchFfrEnergyVolumes;
